import { killerPerkDetails } from '@/data/killerPerkDetails'
import { offeringGameplayProfile } from '@/data/offeringKnowledge'
import { officialLoadoutDescriptionsByLocale } from '@/data/officialLoadoutDescriptions'
import { PERK_DESCRIPTION_OVERRIDES, PERK_DETAIL_ALIASES } from '@/data/perkDescriptionOverrides'
import { survivorPerkDetails } from '@/data/survivorPerkDetails'
import { localizedEntryName } from '@/lib/loadoutLocalization'

const MECHANIC_LABELS = {
  charges: 'Charges',
  cost: 'Coût',
  aura: 'Aura',
  auraRange: 'Portée d’aura',
  auraDuration: 'Durée d’aura',
  effect: 'Effet',
  duration: 'Durée',
  range: 'Portée',
  radius: 'Rayon',
  speed: 'Vitesse',
  repairSpeed: 'Vitesse de réparation',
  sabotageSpeed: 'Vitesse de sabotage',
  healingSpeed: 'Vitesse de soin',
  hookStageCount: 'États de crochet requis',
  secret: 'Offrande secrète',
  realm: 'Royaume',
  map: 'Carte'
}

const DESCRIPTION_LOCALES = ['en', 'fr', 'de', 'es']
const MECHANIC_LABELS_LOCALIZED = {
  en: { cost: 'Cost', auraRange: 'Aura range', auraDuration: 'Aura duration', effect: 'Effect', duration: 'Duration', range: 'Range', radius: 'Radius', speed: 'Speed', repairSpeed: 'Repair speed', sabotageSpeed: 'Sabotage speed', healingSpeed: 'Healing speed', hookStageCount: 'Required Hook stages', secret: 'Secret Offering', realm: 'Realm', map: 'Map' },
  de: { charges: 'Ladungen', cost: 'Kosten', auraRange: 'Aura-Reichweite', auraDuration: 'Aura-Dauer', effect: 'Effekt', duration: 'Dauer', range: 'Reichweite', radius: 'Radius', speed: 'Tempo', repairSpeed: 'Reparaturtempo', sabotageSpeed: 'Sabotagetempo', healingSpeed: 'Heiltempo', hookStageCount: 'Benötigte Hakenphasen', secret: 'Geheime Opfergabe', realm: 'Reich', map: 'Karte' },
  es: { charges: 'Cargas', cost: 'Coste', auraRange: 'Alcance de aura', auraDuration: 'Duración de aura', effect: 'Efecto', duration: 'Duración', range: 'Alcance', radius: 'Radio', speed: 'Velocidad', repairSpeed: 'Velocidad de reparación', sabotageSpeed: 'Velocidad de sabotaje', healingSpeed: 'Velocidad de curación', hookStageCount: 'Fases de gancho requeridas', secret: 'Ofrenda secreta', realm: 'Reino', map: 'Mapa' },
  ja: { charges: 'チャージ', cost: 'コスト', auraRange: 'オーラ範囲', auraDuration: 'オーラ時間', effect: '効果', duration: '持続時間', range: '範囲', radius: '半径', speed: '速度', repairSpeed: '修理速度', sabotageSpeed: '破壊工作速度', healingSpeed: '治療速度', hookStageCount: '必要なフック段階', secret: '秘密のオファリング', realm: '領域', map: 'マップ' }
}
const BOOLEAN_LABELS = {
  en: ['No', 'Yes'], fr: ['Non', 'Oui'], de: ['Nein', 'Ja'], es: ['No', 'Sí'], ja: ['いいえ', 'はい']
}
const DESCRIPTION_FALLBACK = {
  en: 'No official narrative is available; the structured gameplay values below are the local reference.',
  fr: 'Aucun texte narratif officiel n’est disponible ; les valeurs de jeu structurées ci-dessous servent de référence locale.',
  de: 'Kein offizieller Beschreibungstext ist verfügbar; die strukturierten Spielwerte unten dienen als lokale Referenz.',
  es: 'No hay texto narrativo oficial disponible; los valores de juego estructurados siguientes son la referencia local.',
  ja: '公式説明文がありません。以下の構造化されたゲーム数値を参照してください。'
}
const UNRESOLVED_VALUE = {
  en: 'the in-game value', fr: 'la valeur indiquée en jeu', de: 'der im Spiel angezeigte Wert', es: 'el valor indicado en el juego', ja: 'ゲーム内の表示値'
}
const descriptionIndexes = {}

function localeKey (locale) {
  const language = String(locale || 'en').split('-')[0].toLowerCase()
  return BOOLEAN_LABELS[language] ? language : 'en'
}

function normalizedName (value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘`]/g, "'")
    .replace(/[^a-zA-Z0-9]+/g, '')
    .toLowerCase()
}

function descriptionIndex (locale) {
  if (descriptionIndexes[locale]) return descriptionIndexes[locale]
  const descriptions = officialLoadoutDescriptionsByLocale[locale] || {}
  descriptionIndexes[locale] = Object.keys(descriptions).reduce((index, name) => {
    index[normalizedName(name)] = descriptions[name]
    return index
  }, {})
  return descriptionIndexes[locale]
}
function humanize (value) {
  return String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, character => character.toUpperCase())
}

const UNIT_TRANSLATIONS = {
  fr: { second: 'seconde', seconds: 'secondes', metre: 'mètre', metres: 'mètres', meter: 'mètre', meters: 'mètres', charges: 'charges' },
  de: { second: 'Sekunde', seconds: 'Sekunden', metre: 'Meter', metres: 'Meter', meter: 'Meter', meters: 'Meter', charges: 'Ladungen' },
  es: { second: 'segundo', seconds: 'segundos', metre: 'metro', metres: 'metros', meter: 'metro', meters: 'metros', charges: 'cargas' },
  ja: { second: '秒', seconds: '秒', metre: 'メートル', metres: 'メートル', meter: 'メートル', meters: 'メートル', charges: 'チャージ' }
}
const GAME_TERM_TRANSLATIONS = {
  fr: { 'Exit Gates': 'Portes de sortie', 'Health State': 'État de santé', Exhausted: 'Épuisement', Haste: 'Célérité', Survivors: 'Survivants', Survivor: 'Survivant', Killer: 'Tueur', 'Fog Vial': 'Fiole de Brouillard', 'Fog Cloud': 'Nuage de Brouillard' },
  de: { 'Exit Gates': 'Ausgangstore', 'Health State': 'Gesundheitsstatus', Exhausted: 'Erschöpfung', Haste: 'Eile', Survivors: 'Überlebende', Survivor: 'Überlebender', Killer: 'Killer' },
  es: { 'Exit Gates': 'Puertas de salida', 'Health State': 'Estado de salud', Exhausted: 'Agotamiento', Haste: 'Celeridad', Survivors: 'Supervivientes', Survivor: 'Superviviente', Killer: 'Asesino' },
  ja: { 'Exit Gates': '脱出ゲート', 'Health State': '健康状態', Exhausted: '疲労', Haste: '迅速', Survivors: '生存者', Survivor: '生存者', Killer: '殺人鬼' }
}
const LANGUAGE_MARKERS = {
  fr: /\b(le|la|les|de|des|une|vous|votre|augmente|réduit|pendant|lorsque|durée|effet)\b/gi,
  de: /\b(der|die|das|den|des|und|wird|werden|erhöht|verringert|während|dauer|effekt)\b/gi,
  es: /\b(el|la|los|las|de|una|tu|aumenta|reduce|durante|cuando|duración|efecto)\b/gi
}

function localizedValue (value, locale) {
  const language = localeKey(locale)
  const translations = UNIT_TRANSLATIONS[language]
  if (!translations) return String(value)
  return String(value).replace(/\b(seconds?|metres?|meters?|charges)\b/gi, unit => translations[unit.toLowerCase()] || unit)
}

function localizeGameTerms (description, locale) {
  const language = localeKey(locale)
  const terms = GAME_TERM_TRANSLATIONS[language] || {}
  return Object.keys(terms).reduce((text, source) => text.split(source).join(terms[source]), localizedValue(description, language))
}

function descriptionMatchesLocale (description, locale) {
  const language = localeKey(locale)
  if (!description || language === 'en') return Boolean(description)
  if (language === 'ja') return /[\u3040-\u30ff\u3400-\u9fff]/.test(description)
  const marker = LANGUAGE_MARKERS[language]
  if (!marker) return false
  const localMatches = description.match(marker) || []
  const englishMatches = description.match(/\b(the|this|that|your|increases|reduces|while|when|duration|effect)\b/gi) || []
  return localMatches.length > 0 && localMatches.length >= englishMatches.length
}
function printableValue (value, locale) {
  if (typeof value === 'boolean') return BOOLEAN_LABELS[localeKey(locale)][value ? 1 : 0]
  if (Array.isArray(value)) return value.map(entry => localizedValue(entry, locale)).join(' / ')
  return localizedValue(value, locale)
}

export function officialSourceUrl (entry, locale, type) {
  const language = localeKey(locale)
  const sourceLanguage = DESCRIPTION_LOCALES.indexOf(language) !== -1 ? language : 'en'
  const prefix = sourceLanguage === 'en' ? '' : `/${sourceLanguage}`
  const title = String(localizedEntryName(entry, type, sourceLanguage) || (entry && (entry.sourceName || entry.name)) || '')
    .trim()
    .replace(/\s+/g, '_')
  return title ? `https://deadbydaylight.wiki.gg${prefix}/wiki/${encodeURIComponent(title)}` : `https://deadbydaylight.wiki.gg${prefix}/wiki/Dead_by_Daylight_Wiki`
}

export function mechanicRows (entry, locale) {
  const language = localeKey(locale)
  const mechanics = (entry && entry.mechanics) || {}
  const rows = Object.keys(mechanics)
    .filter(key => mechanics[key] !== null && mechanics[key] !== undefined && mechanics[key] !== false)
    .map(key => ({
      key,
      label: ((language === 'fr' ? MECHANIC_LABELS : MECHANIC_LABELS_LOCALIZED[language]) || {})[key] || humanize(key),
      value: printableValue(mechanics[key], language)
    }))
  if (language === 'en' && entry && Array.isArray(entry.tags) && entry.tags.length) {
    rows.push({
      key: 'tags',
      label: 'Related mechanics',
      value: entry.tags.map(humanize).join(' · ')
    })
  }
  return rows
}

function perkDescription (entry, side, locale) {
  const language = localeKey(locale)
  const details = side === 'killer' ? killerPerkDetails : survivorPerkDetails
  const id = entry && entry.id
  const alias = PERK_DETAIL_ALIASES[id]
  const match = PERK_DESCRIPTION_OVERRIDES[id] || details[id] || details[alias]
  if (language !== 'en') {
    const localized = loadoutDescription(entry, 'perk', language)
    if (localized) return localized
    return match ? localizeGameTerms(match.officialDescription, language) : DESCRIPTION_FALLBACK[language]
  }
  return match ? match.officialDescription : ''
}

function loadoutDescription (entry, type, locale) {
  if (!entry) return ''
  const language = localeKey(locale)
  if (DESCRIPTION_LOCALES.indexOf(language) === -1) return ''
  const descriptions = officialLoadoutDescriptionsByLocale[language] || {}
  const sourceName = localizedEntryName(entry, type, language) || entry.sourceName || entry.name
  const candidates = [sourceName, entry.displayName, entry.sourceName, entry.name].filter(Boolean)
  let description = candidates.map(name => descriptions[name]).find(Boolean)
  if (!description) {
    const normalized = descriptionIndex(language)
    description = candidates.map(name => normalized[normalizedName(name)]).find(Boolean)
  }
  description = (description || '').split('Example layoutnumber')[0]
  if (!descriptionMatchesLocale(description, language)) return ''
  const mechanics = entry.mechanics || {}
  const tierValues = Array.isArray(entry.values) ? entry.values : []
  const resolved = description
    .replace(/#(?:name|pn)\b/g, sourceName)
    .replace(/#([A-Za-z0-9_]+)(?:\((\d+)\))?/g, (placeholder, key, rawIndex) => {
      const mechanic = mechanics[key] !== undefined ? mechanics[key] : (key === 'effect' ? mechanics.effet : undefined)
      if (mechanic === undefined) {
        if (tierValues.length && key === 'pl') return tierValues.map(value => localizedValue(value, language)).join(' / ')
        return UNRESOLVED_VALUE[language]
      }
      if (Array.isArray(mechanic) && rawIndex) return localizedValue(mechanic[Math.max(0, Number(rawIndex) - 1)] || placeholder, language)
      return Array.isArray(mechanic) ? mechanic.map(value => localizedValue(value, language)).join(' / ') : localizedValue(mechanic, language)
    })
  return localizeGameTerms(resolved, language)
}

const STAT_STATUS = {
  en: { belowMinimum: 'Insufficient', saturated: 'Saturated', optimal: 'Optimal', useful: 'Useful', weak: 'Weak' },
  fr: { belowMinimum: 'Critère insuffisant', saturated: 'Plafond atteint', optimal: 'Zone optimale', useful: 'Utile', weak: 'Faible' },
  de: { belowMinimum: 'Unzureichend', saturated: 'Maximum erreicht', optimal: 'Optimalbereich', useful: 'Nützlich', weak: 'Schwach' },
  es: { belowMinimum: 'Insuficiente', saturated: 'Máximo alcanzado', optimal: 'Zona óptima', useful: 'Útil', weak: 'Débil' },
  ja: { belowMinimum: '不足', saturated: '上限到達', optimal: '最適域', useful: '有用', weak: '低い' }
}
const STAT_PRIORITY = {
  en: { critical: 'Required', important: 'Important', secondary: 'Secondary', comfort: 'Comfort' },
  fr: { critical: 'Indispensable', important: 'Important', secondary: 'Secondaire', comfort: 'Confort' },
  de: { critical: 'Erforderlich', important: 'Wichtig', secondary: 'Sekundär', comfort: 'Komfort' },
  es: { critical: 'Indispensable', important: 'Importante', secondary: 'Secundario', comfort: 'Comodidad' },
  ja: { critical: '必須', important: '重要', secondary: '補助', comfort: '快適性' }
}

function packageStatAnalysis (entry, locale) {
  const insights = entry && entry.correlation && entry.correlation.statInsights
  if (!Array.isArray(insights)) return []
  const language = localeKey(locale)
  return insights
    .slice(0, 5)
    .map(insight => {
      const label = (insight.labels && (insight.labels[language] || insight.labels.en)) || insight.metricId
      const note = (insight.notes && (insight.notes[language] || insight.notes.en)) || ''
      const status = (STAT_STATUS[language] || STAT_STATUS.en)[insight.status] || insight.status
      const priorityLabel = (STAT_PRIORITY[language] || STAT_PRIORITY.en)[insight.priority] || insight.priority
      const value = `${insight.value}${insight.suffix || ''}`
      return {
        metricId: insight.metricId,
        label,
        note,
        status,
        priority: insight.priority,
        priorityLabel,
        requirementGap: insight.requirementGap,
        criticalGap: insight.criticalGap,
        value,
        utility: insight.utility,
        weight: insight.weight
      }
    })
}

function scoringNote (entry, type, locale) {
  const french = String(locale || '').startsWith('fr')
  if (!entry) return ''
  const strategy = entry.correlation && entry.correlation.strategy
  const strategyDefinition = strategy && (strategy.definition || strategy)
  if (strategyDefinition) {
    const language = localeKey(locale)
    const description = strategyDefinition.descriptions &&
      (strategyDefinition.descriptions[language] || strategyDefinition.descriptions.en)
    const condition = strategyDefinition.conditions &&
      (strategyDefinition.conditions[language] || strategyDefinition.conditions.en)
    if (description) return [description, condition].filter(Boolean).join(' ')
  }

  if (type === 'addon' && entry.itemsCategory === 3) {
    const brightnessOnly = ['power_bulb', 'tir_optic', 'intense_halogen', 'odd_bulb'].indexOf(entry.id) !== -1
    if (brightnessOnly) {
      return french
        ? 'La luminosité visuelle vaut 0 dans le score. Seules la durée d’aveuglement et les éventuelles contreparties sont valorisées.'
        : 'Visual brightness is worth 0 in the score. Only Blind duration and actual trade-offs receive value.'
    }
    if (['leather_grip', 'rubber_grip'].indexOf(entry.id) !== -1) {
      return french
        ? 'La précision aide légèrement la régularité, mais son poids est plafonné : elle ne remplace ni la portée, ni l’autonomie, ni le timing.'
        : 'Accuracy only adds a small consistency benefit; its weight is capped below reach, battery economy and timing.'
    }
  }

  if ((type === 'item' && entry.category === 6) || (type === 'addon' && entry.itemsCategory === 6)) {
    return french
      ? 'Les charges sont valorisées aux seuils de soins complets. La vitesse est convertie en secondes réellement gagnées et perd de la valeur marginale aux hauts pourcentages.'
      : 'Charges are valued at complete-heal breakpoints. Healing speed is converted into actual seconds saved and loses marginal value at high percentages.'
  }

  if ((type === 'item' && entry.category === 7) || (type === 'addon' && entry.itemsCategory === 7)) {
    return french
      ? 'Les bonus d’add-ons restent additifs en jeu. La roulette les convertit en progression et en secondes, puis plafonne leur valeur pratique ; en sabotage, le gain devient marginal sous environ 1,3 s.'
      : 'Item Add-on bonuses remain additive in-game. The roulette converts them into progress and seconds, then caps practical value; for sabotage, gains become marginal below about 1.3 seconds.'
  }

  if (type === 'offering') {
    const profile = offeringGameplayProfile(entry)
    if (profile) {
      return french
        ? `Effet comparé aux offrandes de la même famille : niveau ${profile.rank}. À chaos 0, une version strictement inférieure est exclue.`
        : `Compared against Offerings in the same family: rank ${profile.rank}. At zero Chaos, strictly weaker versions are excluded.`
    }
  }

  return french
    ? 'La roulette utilise les mécaniques et le scénario du build ; la rareté seule n’ajoute aucun bonus.'
    : 'The roulette uses mechanics and build scenario; rarity alone never grants a score bonus.'
}

export function officialEntryProfile (entry, type, side, locale) {
  const language = localeKey(locale)
  const offeringProfile = type === 'offering' ? offeringGameplayProfile(entry) : null
  const description = type === 'perk'
    ? perkDescription(entry, side, language)
    : loadoutDescription(entry, type, language)
  return {
    description: description || (language === 'en' && offeringProfile ? offeringProfile.effect : '') || DESCRIPTION_FALLBACK[language],
    mechanics: mechanicRows(entry, language),
    analysis: packageStatAnalysis(entry, language),
    sourceUrl: officialSourceUrl(entry, language, type),
    scoringNote: scoringNote(entry, type, language),
    localizedDescription: Boolean(description)
  }
}
